import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { LogOut, User, Shield, Building2, LayoutDashboard, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { switchTenant } from '../utils/tenantUtils';
import { globalUsers } from '../data/tenantsData';
import { useBranding } from '../context/BrandingContext';
import apiService from '../services/apiService';

import i18next from 'i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin, isGlobalAdmin, currentTenant, availableCompanies } = useAuth();
  const { getCurrentBranding, getThemeClasses, forceUpdate } = useBranding();
  const [showMenu, setShowMenu] = React.useState(false);
  const [showTenantSelector, setShowTenantSelector] = React.useState(false);
  const [currentLang, setCurrentLang] = React.useState('es');
  
  // Detectar idioma actual de Google Translate
  React.useEffect(() => {
    const detectLanguage = () => {
      // Revisar cookie de Google Translate
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'googtrans') {
          // Cookie format: /es/en o /es/pt
          const lang = value.split('/')[2];
          if (lang && ['es', 'en', 'pt'].includes(lang)) {
            setCurrentLang(lang);
            return;
          }
        }
      }
      setCurrentLang('es'); // Default
    };
    
    detectLanguage();
    
    // Re-detectar después de cambios de idioma
    const interval = setInterval(detectLanguage, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Debug: Ver qué empresas están disponibles
  React.useEffect(() => {
    console.log('📊 Header - Estado actual:', {
      user: user?.email,
      userName: user?.name,
      userAgency: user?.agency,
      currentTenant,
      isGlobalAdmin,
      availableCompanies: availableCompanies,
      companiesLength: availableCompanies?.length
    });
  }, [user, currentTenant, isGlobalAdmin, availableCompanies]);
  
  // Carta operativa del usuario desde la base de datos
  const cartaOperativa = user?.carta_operativa ? {
    name: 'Carta-Operativa.pdf',
    url: user.carta_operativa.startsWith('http') 
      ? user.carta_operativa 
      : `${apiService.baseURL}${user.carta_operativa}`
  } : null;
  
  const navigate = require('react-router-dom').useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleTenantSwitch = (tenantId) => {
    console.log('🔄 Switching tenant to:', tenantId);
    switchTenant(tenantId);
    // Forzar actualización del branding
    forceUpdate();
  };

  const getCompanyName = () => {
    if (isGlobalAdmin) return 'Sistema Global';
    return currentTenant === 'chirakChile' ? 'Chirak Turismo Chile' : 'Chirak Turismo Perú';
  };

  // Obtener información del usuario desde globalUsers
  const getUserInfo = () => {
    const userInfo = globalUsers.find(u => u.email === user?.email);
    if (!userInfo) return null;
    
    return {
      country: userInfo.country,
      agency: userInfo.agency,
      agencyType: userInfo.agencyType
    };
  };

  const getUserDescription = () => {
    if (isGlobalAdmin) {
      return 'Administrador Global';
    }
    
    const userInfo = getUserInfo();
    if (userInfo) {
      if (userInfo.agencyType === 'internal') {
        return `${userInfo.agency} (${userInfo.country})`;
      } else {
        return `${userInfo.agency} - ${userInfo.country}`;
      }
    }
    
    return `Agente en ${getCompanyName()}`;
  };

  // Obtener agencia para mostrar en el header
  const getAgencyName = () => {
    // Primero intentar desde el campo agency del usuario (viene del backend)
    if (user?.agency) {
      return user.agency;
    }
    
    // Fallback: usar getUserInfo
    const userInfo = getUserInfo();
    if (userInfo?.agency) {
      return userInfo.agency;
    }
    
    // Último fallback: descripción completa
    return getUserDescription();
  };

  // Estado para mostrar el avatar ampliado
  const [showAvatar, setShowAvatar] = React.useState(false);

  // Construir URL completa del avatar
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    return `${apiService.baseURL}${avatar}`;
  };

  return (
    <>
      <motion.header
        className="bg-white/95 backdrop-blur-lg border-b border-gray-200/50 sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 relative">
        {/* Selector de idioma con Google Translate automático */}
        <div className="absolute left-1/2 top-2 -translate-x-1/2 z-20 flex gap-1 sm:gap-2 items-center justify-center">
          {getCurrentBranding().showLanguageEs !== false && (
            <button 
              type="button" 
              aria-label="Español" 
              onClick={() => {
                console.log('🇪🇸 Cambiando a Español');
                if (window.changeLanguage) {
                  window.changeLanguage('es');
                }
              }} 
              className={`p-0.5 rounded-full border-2 transition-all hover:border-blue-500 hover:shadow-lg active:scale-95 ${
                currentLang === 'es' ? 'border-blue-600 ring-2 ring-blue-300' : 'border-transparent'
              }`}
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}
              title="Español"
            >
              <img src="/flags/es.svg" alt="Español" style={{ width: 20, height: 20, borderRadius: '50%' }} />
            </button>
          )}
          {getCurrentBranding().showLanguageEn !== false && (
            <button 
              type="button" 
              aria-label="English" 
              onClick={() => {
                console.log('🇺🇸 Cambiando a English');
                if (window.changeLanguage) {
                  window.changeLanguage('en');
                }
              }} 
              className={`p-0.5 rounded-full border-2 transition-all hover:border-blue-500 hover:shadow-lg active:scale-95 ${
                currentLang === 'en' ? 'border-blue-600 ring-2 ring-blue-300' : 'border-transparent'
              }`}
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}
              title="Translate to English"
            >
              <img src="/flags/us.svg" alt="English" style={{ width: 20, height: 20, borderRadius: '50%' }} />
            </button>
          )}
          {getCurrentBranding().showLanguagePt !== false && (
            <button 
              type="button" 
              aria-label="Português" 
              onClick={() => {
                console.log('🇧🇷 Cambiando a Português');
                if (window.changeLanguage) {
                  window.changeLanguage('pt');
                }
              }} 
              className={`p-0.5 rounded-full border-2 transition-all hover:border-blue-500 hover:shadow-lg active:scale-95 ${
                currentLang === 'pt' ? 'border-blue-600 ring-2 ring-blue-300' : 'border-transparent'
              }`}
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}
              title="Português"
            >
              <img src="/flags/br.svg" alt="Português" style={{ width: 20, height: 20, borderRadius: '50%' }} />
            </button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center h-auto sm:h-16 gap-2 sm:gap-0 pt-10 sm:pt-0">
          <div 
            className="flex items-center gap-2 sm:gap-3 justify-center sm:justify-start cursor-pointer"
            onClick={() => {
              navigate('/');
              window.location.reload();
            }}
          >
            <motion.div className="flex items-center gap-2 sm:gap-3" whileHover={{ scale: 1.02 }}>
              {getCurrentBranding().logo ? (
                <img 
                  src={getCurrentBranding().logo} 
                  alt={getCompanyName()} 
                  className="w-12 h-12 sm:w-14 sm:h-14 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${getThemeClasses().gradient} rounded-xl flex items-center justify-center`}>
                  <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  {getCompanyName()}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 hidden sm:block">Plataforma de Turismo y Hotelería</p>
              </div>
            </motion.div>
          </div>
          <div className="flex flex-col xs:flex-row items-center gap-2 sm:gap-4">
            <div className="flex flex-row items-center gap-2 sm:gap-4">
              {/* Selector de Tenant - Solo para agentes con múltiples empresas */}
              {!isGlobalAdmin && availableCompanies.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setShowTenantSelector(!showTenantSelector)}
                    className="flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg font-medium text-sm transition-colors hover:bg-indigo-100"
                  >
                    <Building2 className="w-4 h-4" />
                    <span className="hidden sm:inline">{getCompanyName()}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  
                  {showTenantSelector && (
                    <div className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-lg border border-gray-200 p-2 min-w-[200px] z-50">
                      {availableCompanies.map((company) => (
                        <button
                          key={company.id}
                          onClick={() => {
                            handleTenantSwitch(company.id);
                            setShowTenantSelector(false);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                            company.id === currentTenant 
                              ? 'bg-blue-50 text-blue-700 font-semibold'
                              : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div>
                            <p className="font-medium">{company.name}</p>
                            <p className="text-xs text-gray-500">{company.role}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isAdmin && (
                <Link to="/admin">
                  <motion.button
                    title="Panel Admin"
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all"
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6" />
                    <span className="hidden xs:inline">{t('nav.dashboard')}</span>
                  </motion.button>
                </Link>
              )}
              <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-1 sm:py-2 bg-gray-50 rounded-xl">
                {/* Avatar del usuario con hover/click para ampliar */}
                {user?.avatar && (
                  <div className="relative">
                    <img
                      src={getAvatarUrl(user.avatar)}
                      alt="Avatar"
                      className="w-8 h-8 rounded-full object-cover border cursor-pointer"
                      onMouseEnter={() => setShowAvatar(true)}
                      onMouseLeave={() => setShowAvatar(false)}
                    />
                    {showAvatar && (
                      <div
                        className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 p-2 z-50"
                        onMouseEnter={() => setShowAvatar(true)}
                        onMouseLeave={() => setShowAvatar(false)}
                        style={{ minWidth: '170px' }}
                      >
                        <img src={getAvatarUrl(user.avatar)} alt="Avatar grande" className="w-44 h-44 rounded-2xl object-cover" />
                      </div>
                    )}
                  </div>
                )}
                <div className="text-xs sm:text-sm flex flex-col items-start">
                  <div className="relative">
                    <p
                      className="font-semibold text-gray-900 truncate max-w-[80px] sm:max-w-none cursor-pointer"
                      onClick={() => setShowMenu((v) => !v)}
                    >
                      {user?.name}
                    </p>
                    {showMenu && (
                      <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 p-4 z-50 min-w-[200px] flex flex-col items-center">
                        <p className="font-bold text-gray-800 mb-2">Mi Perfil</p>
                        <p className="text-sm text-gray-600 mb-1">{user?.email}</p>
                        <p className="text-xs text-gray-500 mb-3">
                          {getUserDescription()}
                        </p>
                        {cartaOperativa ? (
                          <a
                            href={cartaOperativa.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition mb-2"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-8m0 8l-4-4m4 4l4-4m-8 8h8a2 2 0 002-2V6a2 2 0 00-2-2H8a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Carta Operativa
                          </a>
                        ) : (
                          <span className="text-gray-400 mb-2">Sin carta operativa</span>
                        )}
                        <button
                          className="text-xs text-gray-500 hover:text-blue-600 mt-2"
                          onClick={() => setShowMenu(false)}
                        >{t('common.close')}</button>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs block truncate max-w-[120px] sm:max-w-none">
                    {getAgencyName()}
                  </p>
                </div>
                <motion.button
                  onClick={handleLogout}
                  title={t('logout')}
                  className="ml-2 p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <LogOut className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </motion.header>
    </>
  );
}

export default Header;